import React, { useState, useEffect } from 'react';
import axios from 'axios';
import OrganizerLayout from '../../components/layouts/OrganizerLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { 
  MessageSquare, Plus, Loader2, Eye, Send, 
  Clock, CheckCircle2, AlertCircle, XCircle,
  RefreshCw, Upload, Paperclip, Calendar
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OrganizerSupport = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [responseText, setResponseText] = useState('');
  const [sending, setSending] = useState(false);
  
  // New Ticket Form
  const [ticketForm, setTicketForm] = useState({
    subject: '',
    description: '',
    priority: 'normal',
    category: 'general'
  });
  const [attachments, setAttachments] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/support/tickets`);
      setTickets(response.data);
    } catch (error) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async () => {
    if (!ticketForm.subject.trim() || !ticketForm.description.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API_URL}/api/support/tickets`, ticketForm);
      const ticketId = response.data.id;

      // Upload attachments if any
      for (const attachment of attachments) {
        await axios.post(
          `${API_URL}/api/support/tickets/${ticketId}/attachments?file_name=${encodeURIComponent(attachment.name)}&file_type=${encodeURIComponent(attachment.type)}&file_data=${encodeURIComponent(attachment.data)}`
        );
      }

      toast.success('Ticket submitted successfully');
      setCreateDialogOpen(false);
      setTicketForm({ subject: '', description: '', priority: 'normal', category: 'general' });
      setAttachments([]);
      fetchTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setAttachments(prev => [...prev, {
          name: file.name,
          type: file.type,
          data: reader.result
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeAttachment = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const viewTicket = async (ticket) => {
    try {
      const response = await axios.get(`${API_URL}/api/support/tickets/${ticket.id}`);
      setSelectedTicket(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load ticket');
    }
  };

  const handleSendResponse = async () => {
    if (!responseText.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setSending(true);
    try {
      const updated = await axios.post(
        `${API_URL}/api/support/tickets/${selectedTicket.id}/respond`,
        { message: responseText }
      );
      setSelectedTicket(updated.data);
      setResponseText('');
      toast.success('Response sent');
      fetchTickets();
    } catch (error) {
      toast.error('Failed to send response');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-PH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const config = {
      open: { icon: AlertCircle, className: 'bg-blue-100 text-blue-700', label: 'Open' },
      in_progress: { icon: Clock, className: 'bg-amber-100 text-amber-700', label: 'In Progress' },
      resolved: { icon: CheckCircle2, className: 'bg-green-100 text-green-700', label: 'Resolved' },
      closed: { icon: XCircle, className: 'bg-gray-100 text-gray-700', label: 'Closed' }
    };
    const c = config[status] || config.open;
    const Icon = c.icon;
    return (
      <Badge className={c.className}>
        <Icon className="h-3 w-3 mr-1" />
        {c.label}
      </Badge>
    );
  };

  const getPriorityBadge = (priority) => {
    const config = {
      low: { className: 'bg-gray-100 text-gray-700', label: 'Low' },
      normal: { className: 'bg-blue-100 text-blue-700', label: 'Normal' },
      high: { className: 'bg-amber-100 text-amber-700', label: 'High' },
      urgent: { className: 'bg-red-100 text-red-700', label: 'Urgent' }
    };
    const c = config[priority] || config.normal;
    return <Badge className={c.className}>{c.label}</Badge>;
  };

  return (
    <OrganizerLayout>
      <div className="space-y-6" data-testid="organizer-support-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-maroon flex items-center gap-3">
              <MessageSquare className="h-8 w-8" />
              Support
            </h1>
            <p className="text-gray-600 mt-1">Get help with your reunion management</p>
          </div>
          
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={fetchTickets}
              className="gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
            <Button 
              onClick={() => setCreateDialogOpen(true)}
              className="bg-maroon hover:bg-maroon-hover text-white gap-2"
              data-testid="create-ticket-btn"
            >
              <Plus className="h-4 w-4" />
              New Ticket
            </Button>
          </div>
        </div>

        {/* Tickets List */}
        <Card>
          <CardHeader>
            <CardTitle>My Support Tickets</CardTitle>
            <CardDescription>View and manage your support requests</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-maroon" />
              </div>
            ) : tickets.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <MessageSquare className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>No support tickets yet</p>
                <p className="text-sm">Create a ticket if you need help</p>
              </div>
            ) : (
              <div className="space-y-3">
                {tickets.map((ticket) => (
                  <div 
                    key={ticket.id}
                    className="p-4 border rounded-lg hover:bg-cream-paper transition-colors cursor-pointer"
                    onClick={() => viewTicket(ticket)}
                    data-testid={`ticket-${ticket.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 truncate">{ticket.subject}</h3>
                        <p className="text-sm text-gray-600 truncate mt-1">{ticket.description}</p>
                        <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                          <Calendar className="h-3 w-3" />
                          {formatDate(ticket.created_at)}
                          {ticket.responses?.length > 0 && (
                            <>
                              <span>•</span>
                              <span>{ticket.responses.length} responses</span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        {getPriorityBadge(ticket.priority)}
                        {getStatusBadge(ticket.status)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Ticket Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-maroon">Create Support Ticket</DialogTitle>
            <DialogDescription>
              Describe your issue and we'll get back to you soon
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Subject *</Label>
              <Input
                value={ticketForm.subject}
                onChange={(e) => setTicketForm({ ...ticketForm, subject: e.target.value })}
                placeholder="Brief summary of your issue"
                data-testid="ticket-subject-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select
                  value={ticketForm.category}
                  onValueChange={(value) => setTicketForm({ ...ticketForm, category: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="billing">Billing</SelectItem>
                    <SelectItem value="technical">Technical</SelectItem>
                    <SelectItem value="feature_request">Feature Request</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Priority</Label>
                <Select
                  value={ticketForm.priority}
                  onValueChange={(value) => setTicketForm({ ...ticketForm, priority: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea
                value={ticketForm.description}
                onChange={(e) => setTicketForm({ ...ticketForm, description: e.target.value })}
                placeholder="Describe your issue in detail..."
                rows={4}
                data-testid="ticket-description-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Attachments</Label>
              <div className="border-2 border-dashed rounded-lg p-4 text-center">
                <input
                  type="file"
                  multiple
                  accept="image/*,.pdf,.doc,.docx"
                  onChange={handleFileChange}
                  className="hidden"
                  id="attachment-upload"
                />
                <label htmlFor="attachment-upload" className="cursor-pointer">
                  <Upload className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-sm text-gray-600">Click to upload files</p>
                  <p className="text-xs text-gray-400">Images, PDF, DOC up to 5MB</p>
                </label>
              </div>
              {attachments.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {attachments.map((att, idx) => (
                    <Badge key={idx} variant="secondary" className="flex items-center gap-1">
                      <Paperclip className="h-3 w-3" />
                      {att.name}
                      <button onClick={() => removeAttachment(idx)} className="hover:text-red-500 ml-1">
                        <XCircle className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateTicket}
              disabled={submitting}
              className="bg-maroon hover:bg-maroon-hover text-white"
              data-testid="submit-ticket-btn"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Submit Ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Ticket Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading text-maroon">Ticket Details</DialogTitle>
          </DialogHeader>

          {selectedTicket && (
            <div className="space-y-4 overflow-y-auto flex-1 pr-2">
              {/* Ticket Info */}
              <div className="bg-cream-paper rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-semibold text-lg">{selectedTicket.subject}</h3>
                  <div className="flex items-center gap-2">
                    {getPriorityBadge(selectedTicket.priority)}
                    {getStatusBadge(selectedTicket.status)}
                  </div>
                </div>
                
                <p className="text-sm text-gray-500 mb-3">
                  Submitted on {formatDate(selectedTicket.created_at)}
                </p>

                <p className="text-gray-700 whitespace-pre-wrap">{selectedTicket.description}</p>

                {/* Attachments */}
                {selectedTicket.attachments?.length > 0 && (
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-xs text-gray-500 mb-2">Attachments</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedTicket.attachments.map((att, idx) => (
                        <a 
                          key={idx}
                          href={att.file_data}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-white border px-3 py-1 rounded text-sm hover:bg-gray-50"
                        >
                          <Paperclip className="h-3 w-3 inline mr-1" />
                          {att.file_name}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Conversation */}
              {selectedTicket.responses?.length > 0 && (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600 font-medium">Conversation</p>
                  {selectedTicket.responses.map((response, idx) => (
                    <div 
                      key={idx}
                      className={`p-3 rounded-lg ${
                        response.is_admin 
                          ? 'bg-maroon/10 border border-maroon/20 ml-8' 
                          : 'bg-gray-100 mr-8'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium">
                          {response.responder_name}
                          {response.is_admin && <Badge className="ml-2 bg-maroon text-white text-xs">Support</Badge>}
                        </span>
                        <span className="text-xs text-gray-500">{formatDate(response.created_at)}</span>
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{response.message}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Reply Form */}
              {selectedTicket.status !== 'closed' && (
                <div className="space-y-2 pt-2 border-t">
                  <p className="text-sm font-medium">Add Response</p>
                  <Textarea
                    value={responseText}
                    onChange={(e) => setResponseText(e.target.value)}
                    placeholder="Type your message..."
                    rows={3}
                  />
                  <Button
                    onClick={handleSendResponse}
                    disabled={sending || !responseText.trim()}
                    className="bg-maroon hover:bg-maroon-hover text-white"
                  >
                    {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                    Send
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </OrganizerLayout>
  );
};

export default OrganizerSupport;
